import axios from 'axios';
import {
  AdminApiKeyItem,
  AdminApiUsageDay,
  AdminApiUsageRequestResponse,
  AdminApiUsageResponse,
  AdminAuditLogResponse,
  AdminCliOperationDetailResponse,
  AdminCliOperationsResponse,
  AdminCliUsageDay,
  AdminContactRequestItem,
  AdminErrorsResponse,
  AdminHealthResponse,
  AdminInstanceItem,
  AdminJobItem,
  AdminOverview,
  AdminWebUsageDay,
  AdminWebUsageJobResponse,
  AdminWebUsageResponse,
  PagedResponse,
} from '../types/admin';
import { ADMIN_LOGIN_NOTICE_KEY, ADMIN_SESSION_EXPIRED_NOTICE } from './adminSession';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8090';

// Axios instance for admin analytics endpoints: attaches the admin token and
// redirects to the login page when the session is no longer valid.
export const adminApi = axios.create({ baseURL: API_BASE_URL });

adminApi.interceptors.request.use((config) => {
  const token = localStorage.getItem('admin_token');
  if (token) {
    config.headers['Authorization'] = `Bearer ${token}`;
  }
  return config;
});

adminApi.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      try {
        localStorage.removeItem('admin_token');
      } catch {
        // ignore storage errors
      }
      if (!window.location.pathname.startsWith('/admin/login')) {
        try {
          sessionStorage.setItem(ADMIN_LOGIN_NOTICE_KEY, ADMIN_SESSION_EXPIRED_NOTICE);
        } catch {
          // ignore storage errors
        }
        window.location.assign('/admin/login');
      }
    }
    return Promise.reject(error);
  },
);

export interface AdminListParams {
  limit?: number;
  offset?: number;
  ids?: string;
  instance_id?: number;
  search?: string;
  sort?: string;
  order?: 'asc' | 'desc';
  from?: string;
  to?: string;
  status?: string;
  operation?: string;
  version?: string;
  // CSV of scopes; a key matches when its scopes contain any of them.
  scope?: string;
  // CSV of API key ids. Absent means every key.
  api_key_ids?: string;
}

function clean(params?: object) {
  if (!params) return undefined;
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== null && v !== '') out[k] = v;
  }
  return out;
}

export const adminApiService = {
  async getOverview(): Promise<AdminOverview> {
    const res = await adminApi.get<AdminOverview>('/api/v1/admin/overview');
    return res.data;
  },

  async getApiUsage(params?: AdminListParams): Promise<AdminApiUsageResponse> {
    const res = await adminApi.get<AdminApiUsageResponse>('/api/v1/admin/usage/api', { params: clean(params) });
    return res.data;
  },

  // One API request plus its usage entries. 404 if unknown.
  async getApiRequest(requestId: string, params?: AdminListParams): Promise<AdminApiUsageRequestResponse> {
    const res = await adminApi.get<AdminApiUsageRequestResponse>('/api/v1/admin/usage/api', {
      params: clean({ ...params, request_id: requestId }),
    });
    return res.data;
  },

  async getApiUsageDaily(params?: { from?: string; to?: string; instance_id?: number; api_key_ids?: string; status?: string }): Promise<{ days: AdminApiUsageDay[] }> {
    const res = await adminApi.get<{ days: AdminApiUsageDay[] }>('/api/v1/admin/usage/api/daily', { params: clean(params) });
    return res.data;
  },

  async getCliOperations(params?: AdminListParams): Promise<AdminCliOperationsResponse> {
    const res = await adminApi.get<AdminCliOperationsResponse>('/api/v1/admin/usage/cli', { params: clean(params) });
    return res.data;
  },

  async getCliOperation(operationId: string, params?: AdminListParams): Promise<AdminCliOperationDetailResponse> {
    const res = await adminApi.get<AdminCliOperationDetailResponse>('/api/v1/admin/usage/cli', {
      params: clean({ ...params, operation_id: operationId }),
    });
    return res.data;
  },

  async getCliUsageDaily(params?: { from?: string; to?: string; instance_id?: number; api_key_ids?: string; operation?: string; status?: string; version?: string }): Promise<{ days: AdminCliUsageDay[] }> {
    const res = await adminApi.get<{ days: AdminCliUsageDay[] }>('/api/v1/admin/usage/cli/daily', { params: clean(params) });
    return res.data;
  },

  async getWebUsage(params?: AdminListParams): Promise<AdminWebUsageResponse> {
    const res = await adminApi.get<AdminWebUsageResponse>('/api/v1/admin/usage/web', { params: clean(params) });
    return res.data;
  },

  // One web job and a page of its entries.
  async getWebJob(jobId: string, params?: AdminListParams): Promise<AdminWebUsageJobResponse> {
    const res = await adminApi.get<AdminWebUsageJobResponse>('/api/v1/admin/usage/web', {
      params: clean({ ...params, job_id: jobId }),
    });
    return res.data;
  },

  async getWebUsageDaily(params?: { from?: string; to?: string; instance_id?: number; operation?: string }): Promise<{ days: AdminWebUsageDay[] }> {
    const res = await adminApi.get<{ days: AdminWebUsageDay[] }>('/api/v1/admin/usage/web/daily', { params: clean(params) });
    return res.data;
  },

  async getJobs(params?: AdminListParams): Promise<PagedResponse<AdminJobItem>> {
    const res = await adminApi.get<PagedResponse<AdminJobItem>>('/api/v1/admin/jobs', { params: clean(params) });
    return res.data;
  },

  async getInstances(params?: AdminListParams & { mode?: string; active?: string; tier?: string }): Promise<PagedResponse<AdminInstanceItem>> {
    const res = await adminApi.get<PagedResponse<AdminInstanceItem>>('/api/v1/admin/instances', { params: clean(params) });
    return res.data;
  },

  async getApiKeys(params?: AdminListParams): Promise<PagedResponse<AdminApiKeyItem>> {
    const res = await adminApi.get<PagedResponse<AdminApiKeyItem>>('/api/v1/admin/api-keys', { params: clean(params) });
    return res.data;
  },

  async getAuditLog(params?: AdminListParams & { action?: string; resource_type?: string }): Promise<AdminAuditLogResponse> {
    const res = await adminApi.get<AdminAuditLogResponse>('/api/v1/admin/audit-log', { params: clean(params) });
    return res.data;
  },

  async getContactRequests(params?: AdminListParams & { request_type?: string }): Promise<PagedResponse<AdminContactRequestItem>> {
    const res = await adminApi.get<PagedResponse<AdminContactRequestItem>>('/api/v1/admin/contact-requests', { params: clean(params) });
    return res.data;
  },

  // Always the last 24 hours. The endpoint takes no params.
  async getErrors(): Promise<AdminErrorsResponse> {
    const res = await adminApi.get<AdminErrorsResponse>('/api/v1/admin/errors');
    return res.data;
  },

  async getHealth(): Promise<AdminHealthResponse> {
    // 503 carries a valid {status: "unhealthy", error} body — surface it
    // instead of failing the query.
    const res = await adminApi.get<AdminHealthResponse>('/api/v1/admin/health', {
      validateStatus: (status) => status === 200 || status === 503,
    });
    return res.data;
  },
};
